import React, { useEffect, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { getGeoVilles } from "../../../api";
import ProspectMap from "./ProspectMap";

const POP = [
  { v: 2000, l: "Gros bourgs (≥ 2 000 hab.)" },
  { v: 5000, l: "Villes (≥ 5 000 hab.)" },
  { v: 10000, l: "Villes moyennes (≥ 10 000 hab.)" },
  { v: 30000, l: "Grandes villes (≥ 30 000 hab.)" },
];
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(n);

export const useVillesAround = (radius, minPop) => {
  const [villes, setVilles] = useState([]);
  const [siege, setSiege] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      getGeoVilles({ radius_km: radius, min_pop: minPop })
        .then((r) => alive && (setVilles(r.data.villes), setSiege(r.data.siege), setError(null)))
        .catch((e) => alive && setError(e?.response?.data?.detail || "Référentiel indisponible"))
        .finally(() => alive && setLoading(false));
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [radius, minPop]);
  return { villes, siege, loading, error };
};

export const RadiusControls = ({ radius, setRadius, minPop, setMinPop, idPrefix = "villes" }) => (
  <div className="grid grid-cols-2 gap-3 mb-3">
    <div>
      <label className="block text-xs font-semibold text-neutral-600 mb-1">Rayon autour du siège : <b>{radius} km</b></label>
      <input type="range" min={5} max={150} step={5} value={radius} onChange={(e) => setRadius(Number(e.target.value))} className="w-full accent-brand" data-testid={`${idPrefix}-radius`} />
    </div>
    <div>
      <label className="block text-xs font-semibold text-neutral-600 mb-1">Taille des communes</label>
      <select value={minPop} onChange={(e) => setMinPop(Number(e.target.value))} className="w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm" data-testid={`${idPrefix}-minpop`}>
        {POP.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
      </select>
    </div>
  </div>
);

const VillePicker = ({ selected = [], onChange, radius, setRadius, nextVille }) => {
  const [minPop, setMinPop] = useState(5000);
  const { villes, siege, loading, error } = useVillesAround(radius, minPop);
  const isOn = (nom) => selected.some((v) => v.toLowerCase() === nom.toLowerCase());
  const toggle = (nom) => onChange(isOn(nom) ? selected.filter((v) => v.toLowerCase() !== nom.toLowerCase()) : [...selected, nom]);
  const names = villes.map((v) => v.nom);

  return (
    <div className="border border-neutral-200 rounded-lg p-3 bg-neutral-50" data-testid="ville-picker">
      <RadiusControls radius={radius} setRadius={setRadius} minPop={minPop} setMinPop={setMinPop} />
      <ProspectMap siege={siege} radiusKm={radius} villes={villes} selected={selected} onToggle={toggle} />
      <div className="flex items-center justify-between text-xs text-neutral-500 mb-2">
        <span className="flex items-center gap-1"><MapPin size={12} /> {loading ? "Chargement…" : `${villes.length} commune(s) dans le rayon · ${selected.length} sélectionnée(s)`}</span>
        <span className="flex gap-2">
          <button type="button" className="text-red-600 hover:underline" onClick={() => onChange([...new Set([...selected, ...villes.filter((v) => v.zone_blanche).map((v) => v.nom)])])} data-testid="villes-select-blank">
            + Zones blanches ({villes.filter((v) => v.zone_blanche).length})
          </button>
          <button type="button" className="text-brand hover:underline" onClick={() => onChange([...new Set([...selected, ...names])])} data-testid="villes-select-all">Tout sélectionner</button>
          <button type="button" className="text-neutral-500 hover:underline" onClick={() => onChange([])} data-testid="villes-clear">Vider</button>
        </span>
      </div>
      {error && <p className="text-xs text-red-600 mb-2" data-testid="villes-error">{error}</p>}
      {nextVille && <p className="text-xs text-neutral-600 mb-2" data-testid="villes-next">Rotation à tour de rôle — prochaine recherche : <b>{nextVille}</b></p>}
      <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto" data-testid="villes-list">
        {loading && villes.length === 0 && <Loader2 size={16} className="animate-spin text-neutral-400" />}
        {villes.map((v) => (
          <button key={v.code} type="button" onClick={() => toggle(v.nom)} title={`${fmt(v.population)} hab. · ${v.distance_km} km${v.zone_blanche ? " · zone blanche : aucun prospect contacté" : ` · ${v.contacted} prospect(s) contacté(s)`}`}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${isOn(v.nom) ? "bg-brand text-white border-brand" : v.zone_blanche ? "bg-white border-dashed border-red-400 text-neutral-700 hover:border-red-600" : "bg-white border-neutral-300 text-neutral-700 hover:border-brand"}`}
            data-testid={`ville-chip-${v.code}`}>
            {v.nom} <span className={isOn(v.nom) ? "opacity-80" : "text-neutral-400"}>· {v.distance_km} km</span>{v.zone_blanche && !isOn(v.nom) && <span className="text-red-500"> ◌</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

export default VillePicker;
